import { useState, useEffect, useMemo, useCallback } from 'react';
import staticResources from '../data/nil-resources.json';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Star, FileText, Presentation, BookOpen, Calendar, ExternalLink, Upload, Trash2, Loader2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  'case-study': 'Case Study', 'one-pager': 'One-Pager', 'sales-deck': 'Sales Deck',
  'white-paper': 'White Paper', 'article': 'Article', 'availability-report': 'Availability Report',
};
const FILTER_OPTIONS = [
  { key: 'all', label: 'All Resources' },
  { key: 'availability-report', label: 'Availability Reports' },
  { key: 'case-study', label: 'Case Studies' },
  { key: 'one-pager', label: 'One-Pagers' },
  { key: 'sales-deck', label: 'Sales Decks' },
  { key: 'white-paper', label: 'White Papers' },
  { key: 'article', label: 'Articles' },
];
const TYPE_CONFIG = {
  'case-study': { icon: Star, gradient: 'bg-gradient-to-r from-[#4ECDC4] to-[#2BA69F]', chipBg: 'bg-[#E6FAF8]', chipText: 'text-[#2BA69F]' },
  'one-pager': { icon: FileText, gradient: 'bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]', chipBg: 'bg-[#EEF0FF]', chipText: 'text-[#6B5CE7]' },
  'sales-deck': { icon: Presentation, gradient: 'bg-gradient-to-r from-[#6B5CE7] to-[#9B8FFF]', chipBg: 'bg-[#F0EDFF]', chipText: 'text-[#6B5CE7]' },
  'article': { icon: BookOpen, gradient: 'bg-gradient-to-r from-[#FF6B35] to-[#FFD93D]', chipBg: 'bg-[#FFF4ED]', chipText: 'text-[#FF6B35]' },
  'white-paper': { icon: FileText, gradient: 'bg-gradient-to-r from-[#E040FB] to-[#FF6B35]', chipBg: 'bg-[#FDF0FF]', chipText: 'text-[#E040FB]' },
  'availability-report': { icon: Calendar, gradient: 'bg-gradient-to-r from-[#0EA5E9] to-[#4ECDC4]', chipBg: 'bg-[#E0F2FE]', chipText: 'text-[#0284C7]' },
};

const ResourceCard = ({ resource, onDelete }) => {
  const config = TYPE_CONFIG[resource.type] || TYPE_CONFIG['article'];
  const Icon = config.icon;
  const dateFormatted = new Date(resource.dateAdded).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="relative group h-full" data-testid={`nil-resource-${resource.id}`}>
      {resource.source === 'upload' && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(resource); }}
          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-md bg-white/90 border border-[#E2E8F0] flex items-center justify-center text-[#94A3B8] hover:text-[#FF3B30] hover:border-[#FF3B30] transition-colors"
          data-testid={`nil-resource-delete-${resource.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      <Card onClick={() => window.open(resource.contentFile, '_blank', 'noopener,noreferrer')}
        className="bg-white border-[#E2E8F0] overflow-hidden h-full flex flex-col transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(107,92,231,0.15)] cursor-pointer">
        <div className={`h-1 w-full ${config.gradient}`} />
        <CardContent className="p-0 flex flex-col flex-1">
          <div className="p-5 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide ${config.chipBg} ${config.chipText} self-start`}>
                <Icon className="w-3 h-3" /> {TYPE_LABELS[resource.type] || resource.type}
              </span>
              {resource.source === 'upload' && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-[#10B981] bg-[#ECFDF5] px-2 py-0.5 rounded-full">New</span>
              )}
            </div>
            <h3 className="text-[#1A1A1A] font-bold text-base leading-tight mb-2 group-hover:text-[#6B5CE7] transition-colors" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
              {resource.title}
            </h3>
            <p className="text-[#666] text-sm leading-relaxed flex-1 line-clamp-3">{resource.description}</p>
          </div>
          <div className="px-5 py-3 border-t border-[#f0f0f0] flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-xs text-[#999]"><Calendar className="w-3 h-3" />{dateFormatted}</span>
            <span className="flex items-center gap-1 text-sm font-semibold text-[#7B6FE8] group-hover:underline">View <ExternalLink className="w-3 h-3" /></span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const NILResourcesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploaded, setUploaded] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ file: null, title: '', description: '', type: 'one-pager' });
  const [uploading, setUploading] = useState(false);

  const loadUploaded = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/resources`, { headers: getAuthHeaders() });
      setUploaded(res.data.map(r => ({ ...r, contentFile: `${API}/public/resources/${r.id}/view` })));
    } catch { /* ignore */ }
  }, [getAuthHeaders]);

  useEffect(() => { loadUploaded(); }, [loadUploaded]);

  const allResources = useMemo(() => [...uploaded, ...staticResources], [uploaded]);

  const filtered = useMemo(() => {
    let list = activeFilter === 'all' ? allResources : allResources.filter((r) => r.type === activeFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((r) => r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || (r.tags && r.tags.some(t => t.toLowerCase().includes(q))));
    }
    return [...list].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return new Date(b.dateAdded) - new Date(a.dateAdded);
    });
  }, [activeFilter, searchTerm, allResources]);

  const handleUpload = async () => {
    if (!form.file) { toast.error('Choose an HTML file'); return; }
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description are required'); return; }
    const fd = new FormData();
    fd.append('file', form.file);
    fd.append('title', form.title.trim());
    fd.append('description', form.description.trim());
    fd.append('type', form.type);
    setUploading(true);
    try {
      await axios.post(`${API}/resources`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Resource published');
      setForm({ file: null, title: '', description: '', type: 'one-pager' });
      setDialogOpen(false);
      await loadUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (resource) => {
    if (!window.confirm(`Remove "${resource.title}" from the library?`)) return;
    try {
      await axios.delete(`${API}/resources/${resource.id}`, { headers: getAuthHeaders() });
      setUploaded(prev => prev.filter(r => r.id !== resource.id));
      toast.success('Resource removed');
    } catch { toast.error('Failed to remove resource'); }
  };

  return (
    <div className="p-6 space-y-8 bg-[#F8FAFC] min-h-screen">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#334155] p-8 md:p-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#4ECDC4] rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#6B5CE7] rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-3xl">
            <p className="text-[#4ECDC4] font-semibold text-sm uppercase tracking-wider mb-2">Players Health NIL</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>NIL Resource Library</h1>
            <p className="text-[#94A3B8] text-lg max-w-2xl">Case studies, product overviews, and strategic frameworks for NIL risk management.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#4ECDC4] hover:bg-[#3BC0A9] text-[#0F172A] font-bold uppercase tracking-wide shadow-lg whitespace-nowrap" data-testid="upload-resource-btn">
                <Plus className="w-4 h-4 mr-2" /> Upload Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>UPLOAD RESOURCE</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="p-3 bg-[#EEF0FF] border border-[#C7D2FE] rounded-lg text-xs text-[#4338CA]">
                  HTML or PDF files. HTML resources should be built in Claude with the Players Health brand skill so branding stays consistent.
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#475569] text-sm">File (HTML or PDF)</Label>
                  <input type="file" accept=".html,.htm,.pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                    className="text-sm text-[#475569] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#6B5CE7] file:text-white file:text-sm file:font-medium hover:file:bg-[#5B4FD6] file:cursor-pointer w-full"
                    data-testid="resource-file-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#475569] text-sm">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. 2025 NIL Year in Review" className="bg-[#F8FAFC] border-[#E2E8F0]" data-testid="resource-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#475569] text-sm">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description shown on the card" className="bg-[#F8FAFC] border-[#E2E8F0] min-h-[70px]" data-testid="resource-desc-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#475569] text-sm">Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="bg-[#F8FAFC] border-[#E2E8F0]" data-testid="resource-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-[#E2E8F0]">
                      <SelectItem value="availability-report">Availability Report</SelectItem>
                      <SelectItem value="case-study">Case Study</SelectItem>
                      <SelectItem value="one-pager">One-Pager</SelectItem>
                      <SelectItem value="sales-deck">Sales Deck</SelectItem>
                      <SelectItem value="white-paper">White Paper</SelectItem>
                      <SelectItem value="article">Article</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleUpload} disabled={uploading} className="w-full bg-[#6B5CE7] hover:bg-[#5B4FD6] text-white font-bold" data-testid="resource-upload-submit">
                  {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing...</> : <><Upload className="w-4 h-4 mr-2" /> Publish to Library</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Filter Bar */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input placeholder="Search resources..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-[#F8FAFC] border-[#E2E8F0]" data-testid="nil-search-input" />
          </div>
          <div className="flex flex-wrap gap-2 justify-center pt-2 border-t border-[#E2E8F0]">
            {FILTER_OPTIONS.map((opt) => (
              <button key={opt.key} onClick={() => setActiveFilter(opt.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${activeFilter === opt.key ? 'bg-[#7B6FE8] border-[#7B6FE8] text-white' : 'bg-white border-[#E2E8F0] text-[#232323] hover:border-[#7B6FE8] hover:text-[#7B6FE8]'}`}
                data-testid={`nil-filter-${opt.key}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Resource Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((resource) => (
          <ResourceCard key={resource.id} resource={resource} onDelete={handleDelete} />
        ))}
      </div>

      {filtered.length === 0 && (
        <Card className="bg-white border-[#E2E8F0]">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-[#0F172A] font-semibold text-lg mb-2">No resources found</h3>
            <p className="text-[#64748B]">Try adjusting your search or filters</p>
          </CardContent>
        </Card>
      )}

      <div className="text-sm text-[#94A3B8] text-center">
        Showing {filtered.length} of {allResources.length} resources
      </div>
    </div>
  );
};

export default NILResourcesPage;
